import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveCard } from "../components/ResponsiveCard";
import { Button, Form, Input, Select, Space, message } from "antd";
import { PlatformIcon } from "../components/PlatformIcon";
import { Platform } from "../../types/platform";
import { WizardService } from "../../services/WizardService";
import { Mapping } from "../../types/mapping";
import { SdkCategory } from "../../types/sdk";
import { describeCrawlerError } from "./crawlerErrors";

interface AbstractCrawlerCardProps {
  platform: Platform;
}

// Helper function to get the dependency field name dynamically
const getDependencyField = (
  mediation: string,
  platform: Platform
): keyof Mapping | undefined => {
  const platformSuffix = platform === Platform.IOS ? "Ios" : "Android";
  // Handle camelCase for levelPlay
  const mediationKey = mediation === "levelplay" ? "levelPlay" : mediation;
  const fieldName = `${mediationKey}Dependency${platformSuffix}`;

  // Validate that this is a valid dependency field
  const validFields = [
    "appodealDependencyIos",
    "appodealDependencyAndroid",
    "bidmachineDependencyIos",
    "bidmachineDependencyAndroid",
    "bidonDependencyIos",
    "bidonDependencyAndroid",
    "levelPlayDependencyIos",
    "levelPlayDependencyAndroid",
    "maxDependencyIos",
    "maxDependencyAndroid",
    "ownDependencyIos",
    "ownDependencyAndroid",
  ];

  return validFields.includes(fieldName)
    ? (fieldName as keyof Mapping)
    : undefined;
};

export const AbstractCrawlerCard = (props: AbstractCrawlerCardProps) => {
  const api = WizardService.Crawler;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMappingsLoading, setIsMappingsLoading] = useState(true);
  const [mappings, setMappings] = useState<Mapping[]>([]);

  const [form] = Form.useForm();

  // Load mappings on mount
  useEffect(() => {
    WizardService.Mapping.all(new URLSearchParams({ per: "1000" }))
      .then((res) => setMappings(res.items || []))
      .finally(() => setIsMappingsLoading(false));
  }, []);

  // Mediation options (category === MEDIATION)
  const mediationOptions = useMemo(
    () =>
      mappings
        .filter((m) => m.category === SdkCategory.MEDIATION)
        .map((m) => ({ value: m.status, label: m.display || m.status }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [mappings]
  );

  // SDK options - all mappings (including other mediations for Appodeal)
  const sdkOptions = useMemo(
    () =>
      mappings
        .map((m) => ({ value: m.status, label: m.display || m.status }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [mappings]
  );

  // Auto-fill dependency and repository when mediation and SDK are selected
  const onValuesChange = (changed: any, all: any) => {
    // Clear dependent fields when mediation changes
    if ("mediation" in changed) {
      form.resetFields(["dependency", "pod", "repository"]);
    }

    // Only auto-fill when sdk changes (not when user types in dependency field)
    if (!("sdk" in changed)) return;

    const { mediation, sdk } = all;
    if (!mediation || !sdk) return;

    const mapping = mappings.find((m) => m.status === sdk);
    if (!mapping) return;

    // Get dependency field
    const depField = getDependencyField(mediation, props.platform);
    const dependency = depField ? mapping[depField] : undefined;

    // Only set value if found - don't clear manual input
    if (dependency) {
      form.setFieldsValue({
        ...(props.platform === Platform.ANDROID
          ? { dependency }
          : { pod: dependency }),
      });
    }
  };

  const onReset = () => {
    form.resetFields();
  };

  const onSubmit = (values: any) => {
    setIsLoading(true);
    const payload = { ...values };
    if (values.dependency) {
      const [group, artifact] = values.dependency.split(":");
      payload.group = group;
      payload.artifact = artifact;
      delete payload.dependency;
    }
    api
      .adapter(props.platform, payload)
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        message.error(describeCrawlerError(error));
      })
      .finally(() => {
        form.resetFields();
        setIsLoading(false);
      });
  };

  return (
    <ResponsiveCard
      contentPadding="1rem"
      title={
        <Space>
          <PlatformIcon platform={props.platform} />
          Any Adapter
        </Space>
      }
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        onValuesChange={onValuesChange}
      >
        <Form.Item
          name="mediation"
          label="Mediation Status"
          rules={[{ required: true, message: "Please select mediation" }]}
        >
          <Select
            placeholder="Select mediation"
            showSearch
            optionFilterProp="label"
            loading={isMappingsLoading}
            options={mediationOptions}
          />
        </Form.Item>
        <Form.Item
          name="sdk"
          label="Network SDK Status"
          rules={[{ required: true, message: "Please select SDK" }]}
        >
          <Select
            placeholder="Select SDK"
            showSearch
            optionFilterProp="label"
            loading={isMappingsLoading}
            options={sdkOptions}
          />
        </Form.Item>
        <Form.Item
          name="version"
          label="Version"
          extra="~> (up to) operator will be using"
        >
          <Input />
        </Form.Item>
        {props.platform === Platform.ANDROID ? (
          <>
            <Form.Item
              name="dependency"
              label="Dependency"
              extra="Format: group:artifact (e.g. com.example:library)"
              rules={[{ required: true, message: "Please input dependency" }]}
            >
              <Input placeholder="group:artifact" />
            </Form.Item>
          </>
        ) : (
          <Form.Item
            name="pod"
            label="Pod Name"
            rules={[{ required: true, message: "Please input pod name" }]}
          >
            <Input />
          </Form.Item>
        )}
        <Form.Item
          name="repository"
          label="Repository"
          extra={
            props.platform === Platform.IOS
              ? "GitHub repo: owner/repo or https://github.com/owner/repo"
              : "Maven repo: domain/path (e.g. artifactory.appodeal.com/appodeal)"
          }
        >
          <Input
            placeholder={
              props.platform === Platform.IOS
                ? "appodeal/Specs"
                : "artifactory.appodeal.com/appodeal"
            }
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button loading={isLoading} type="primary" htmlType="submit">
              Crawl
            </Button>
            <Button htmlType="button" onClick={onReset}>
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </ResponsiveCard>
  );
};
